import { T } from '../world/tiles';
import { hash2 } from './noise';
import { shade } from './paint';

/** Structural tiles for the trip destinations: glass towers, flat roofs, marble cliffs, the suspension bridge. */
type Ctx = CanvasRenderingContext2D;
type At = (dx: number, dy: number) => string;

const rect = (c: Ctx, col: string, x: number, y: number, w: number, h: number) => {
  c.fillStyle = col;
  c.fillRect(x, y, w, h);
};
function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

export function paint2(c: Ctx, at: At, tx: number, ty: number, ch: string, x: number, y: number): void {
  switch (ch) {
    case 'J':
      return glass(c, at, tx, ty, x, y);
    case 'O':
      return flatRoof(c, at, tx, ty, x, y);
    case 'M':
      return cliffFace(c, at, tx, ty, x, y);
    case 'y':
      return bridge(c, at, x, y);
    case 'q':
      return riverRocks(c, tx, ty, x, y);
  }
}

/** Glass curtain wall: blue-grey panes with mullions and a sky reflection. */
function glass(c: Ctx, at: At, tx: number, ty: number, x: number, y: number): void {
  vgrad(c, x, y, T, T, '#7fa8c8', '#3f6688');
  const lit = hash2(tx, ty, 311);
  for (let j = 0; j < T; j += 4) {
    for (let i = 0; i < T; i += 4) {
      const v = hash2(tx * 4 + i / 4, ty * 4 + j / 4, 312);
      if (v > 0.86) rect(c, `rgba(255,236,170,${0.25 + lit * 0.4})`, x + i + 0.5, y + j + 0.5, 3, 3);
    }
  }
  c.fillStyle = 'rgba(255,255,255,0.18)';
  c.beginPath();
  c.moveTo(x + 2, y + T);
  c.lineTo(x + 8, y);
  c.lineTo(x + 11, y);
  c.lineTo(x + 5, y + T);
  c.fill();
  for (let i = 0; i <= T; i += 4) rect(c, 'rgba(30,45,60,0.55)', x + i, y, 0.5, T);
  for (let j = 0; j <= T; j += 4) rect(c, 'rgba(30,45,60,0.45)', x, y + j, T, 0.5);
  if (at(0, 1) !== 'J') {
    vgrad(c, x, y + T - 3, T, 3, '#9d978b', '#6b665c');
  }
  if (at(-1, 0) !== 'J') rect(c, 'rgba(0,0,0,0.25)', x, y, 1, T);
  if (at(1, 0) !== 'J') rect(c, 'rgba(0,0,0,0.3)', x + T - 1, y, 1, T);
}

/** Flat concrete roof with a parapet, AC units and the odd water tank. */
function flatRoof(c: Ctx, at: At, tx: number, ty: number, x: number, y: number): void {
  vgrad(c, x, y, T, T, '#a8a49c', '#94908a');
  for (let k = 0; k < 4; k++) rect(c, 'rgba(0,0,0,0.06)', x + hash2(tx, ty * 4 + k, 321) * T, y + hash2(tx * 4 + k, ty, 322) * T, 2, 1);
  const h = hash2(tx, ty, 323);
  if (h > 0.82) {
    rect(c, '#d8d6d0', x + 4, y + 5, 7, 5);
    rect(c, '#7a7a78', x + 5, y + 6, 5, 0.6);
    rect(c, '#7a7a78', x + 5, y + 8, 5, 0.6);
  } else if (h > 0.76) {
    c.fillStyle = '#e8e4da';
    c.beginPath();
    c.ellipse(x + 8, y + 8, 4, 3, 0, 0, Math.PI * 2);
    c.fill();
    rect(c, '#b8b2a6', x + 4, y + 8, 8, 0.6);
  }
  if (at(0, -1) !== 'O') rect(c, '#c8c4ba', x, y, T, 2);
  if (at(0, 1) !== 'O') rect(c, '#6e6a64', x, y + T - 2, T, 2);
  if (at(-1, 0) !== 'O') rect(c, '#bcb8ae', x, y, 1.5, T);
  if (at(1, 0) !== 'O') rect(c, '#76726c', x + T - 1.5, y, 1.5, T);
}

/** Marble cliff: where the cliff ends to the south, a striated vertical face drops into the gorge. */
function cliffFace(c: Ctx, at: At, tx: number, ty: number, x: number, y: number): void {
  const below = at(0, 1);
  if (below !== 'M') {
    const top = y + 4;
    vgrad(c, x, top, T, T - 4, '#d6d2c8', '#8a867c');
    for (let i = 0; i < T; i += 2) {
      const v = hash2(tx * 8 + i, ty, 331);
      rect(c, v > 0.5 ? 'rgba(255,255,255,0.35)' : 'rgba(60,55,50,0.28)', x + i, top + v * 3, 0.8, T - 4 - v * 3);
    }
    rect(c, '#f4f2ec', x, top, T, 1);
    rect(c, 'rgba(0,0,0,0.35)', x, y + T - 1, T, 1);
    // Ferns and drips.
    if (hash2(tx, ty, 332) > 0.6) {
      c.fillStyle = '#4f8a3f';
      c.beginPath();
      c.ellipse(x + 4 + hash2(tx, ty, 333) * 8, top + 2, 3, 1.5, 0, 0, Math.PI * 2);
      c.fill();
    }
  }
  if (at(0, -1) !== 'M') rect(c, 'rgba(255,255,255,0.35)', x, y, T, 1);
  if (at(-1, 0) !== 'M') rect(c, 'rgba(0,0,0,0.28)', x, y, 1.5, T);
  if (at(1, 0) !== 'M') rect(c, 'rgba(0,0,0,0.32)', x + T - 1.5, y, 1.5, T);
}

/** Suspension bridge planks with rope rails (runs across the river). */
function bridge(c: Ctx, at: At, x: number, y: number): void {
  const vertical = at(0, -1) === 'y' || at(0, 1) === 'y';
  if (vertical) {
    for (let j = 0; j < T; j += 3) {
      rect(c, j % 6 ? '#a8784a' : '#956a40', x + 3, y + j, T - 6, 2.4);
      rect(c, 'rgba(0,0,0,0.25)', x + 3, y + j + 2.4, T - 6, 0.6);
    }
    rect(c, '#e8d8b0', x + 2, y, 0.8, T);
    rect(c, '#e8d8b0', x + T - 2.8, y, 0.8, T);
    rect(c, 'rgba(0,0,0,0.3)', x + 3, y, 0.5, T);
  } else {
    for (let i = 0; i < T; i += 3) {
      rect(c, i % 6 ? '#a8784a' : '#956a40', x + i, y + 3, 2.4, T - 6);
      rect(c, 'rgba(0,0,0,0.25)', x + i + 2.4, y + 3, 0.6, T - 6);
    }
    rect(c, '#e8d8b0', x, y + 2, T, 0.8);
    rect(c, '#e8d8b0', x, y + T - 2.8, T, 0.8);
  }
  if (at(-1, 0) !== 'y' && !vertical) rect(c, '#5a4030', x, y, 2, T);
  if (at(1, 0) !== 'y' && !vertical) rect(c, '#5a4030', x + T - 2, y, 2, T);
  if (at(0, -1) !== 'y' && vertical) rect(c, '#5a4030', x, y, T, 2);
  if (at(0, 1) !== 'y' && vertical) rect(c, '#5a4030', x, y + T - 2, T, 2);
}

/** A few smooth boulders in the river, with foam around them. */
function riverRocks(c: Ctx, tx: number, ty: number, x: number, y: number): void {
  const h = hash2(tx, ty, 341);
  if (h < 0.78) return;
  const rx = x + 3 + hash2(tx, ty, 342) * 10, ry = y + 3 + hash2(tx, ty, 343) * 10;
  c.fillStyle = 'rgba(240,255,252,0.7)';
  c.beginPath();
  c.ellipse(rx, ry + 1, 4.5, 2.2, 0, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = shade('#c8c4ba', -0.15);
  c.beginPath();
  c.ellipse(rx, ry, 3.2, 2.2, 0, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = '#e8e4da';
  c.beginPath();
  c.ellipse(rx - 0.8, ry - 0.8, 1.6, 1, 0, 0, Math.PI * 2);
  c.fill();
}
